import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { Spin } from 'antd';
import interact from 'interactjs';
import styles from './deviceMap.module.css';
import { useApp } from "../../../model";
import { throttle } from "lodash";
import { useData } from "../../Project/ProjectSummary/DeviceMap";
import { InfoCircleTwoTone } from '@ant-design/icons';
import DevicePoint from "./DevicePoint";

export default function ProjectMap({ project, imgUrl, isStatic }: { isStatic?: boolean, project: Project, imgUrl: string }) {
    const app = useApp()
    const { data, loading } = useData(project, app.deviceMapDataSourceRefreshKey);
    const boundDevices = data?.data.filter(i => !!i.location);
    const maxScale = 5;
    const minScale = 0.5;
    const [scale, _setScale] = useState(1);
    const setScale = (newScale: number) => {
        if (newScale > maxScale) {
            _setScale(maxScale)
        } else if (newScale < minScale) {
            _setScale(minScale)
        } else {
            _setScale(newScale)
        }
    }
    const positionRef = useRef({ x: 0, y: 0 });
    const [imgNaturalAspectRatio, setImgNaturalAspectRatio] = useState(1);
    const scaleRef = useRef(scale)
    const containerRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        scaleRef.current = scale;
    }, [scale])
    const onWheel = useCallback(
        throttle((e: WheelEvent) => {
            const scale = scaleRef.current;
            if (e.deltaY > 0) {
                setScale(scale * 1.05)
            } else {
                setScale(scale / 1.05)
            }
        }, 5)
        , []);
    useEffect(() => {
        if (!containerRef.current || !positionRef.current) return;
        const target = containerRef.current;
        const position = positionRef.current;
        Object.assign(target.style, {
            transform: `scale(${scale}) translate3d(${position.x * scale}px, ${position.y * scale}px, 0)`
        })
    }, [scale]);
    useEffect(() => {
        if (!containerRef.current || isStatic) return;
        interact(containerRef.current).draggable({
            listeners: {
                move(event) {
                    const position = positionRef.current;
                    const scale = scaleRef.current;
                    if (!position) return;
                    position.x += event.dx
                    position.y += event.dy
                    event.target.style.transform =
                        `scale(${scale}) translate3d(${position.x * scale}px, ${position.y * scale}px, 0)`
                }
            }
        });
    }, [isStatic]);

    useEffect(() => {
        if (isStatic) return;
        const cb = (e: WheelEvent) => {
            e.stopPropagation();
            e.preventDefault();
            onWheel(e)
        }
        if (containerRef.current?.parentElement) {
            containerRef.current.parentElement.addEventListener('wheel', cb)
        }
        return () => {
            if (containerRef.current?.parentElement) {
                containerRef.current.parentElement.removeEventListener('wheel', cb)
            }
        }
    }, [onWheel, isStatic]);
    const container = containerRef.current;
    const containerSize = useMemo(() => (
        container ? {
            width: container.clientWidth,
            height: container.clientHeight,
        } : {
            width: 1,
            height: 1,
        }
    ), [container]);
    const playgroundSize = useMemo(() => {
        // imgNaturalAspectRatio = w / h
        if (imgNaturalAspectRatio > 1) {
            // 纵向留白
            let { width } = containerSize;
            let height = width / imgNaturalAspectRatio;
            if (height > containerSize.height) {
                height = containerSize.height;
                width = height * imgNaturalAspectRatio;
            }
            return {
                width,
                height,
            }
        } else {
            // 横向留白
            let { height } = containerSize;
            let width = height * imgNaturalAspectRatio;
            if (width > containerSize.width) {
                width = containerSize.width;
                height = width * imgNaturalAspectRatio;
            }
            return {
                width,
                height,
            }
        }
    }, [containerSize, imgNaturalAspectRatio]);
    useEffect(() => {
        if (!imgUrl) return;
        let img = new Image();
        img.src = imgUrl;
        img.style.objectFit = 'contain';
        img.style.width = containerSize.width + 'px';
        img.style.height = containerSize.height + 'px';
        img.onload = e => {
            const ele = (e.target as HTMLImageElement)
            const naturalAspectRatio = ele.naturalWidth / ele.naturalHeight;
            setImgNaturalAspectRatio(naturalAspectRatio);
        };
    }, [imgUrl, containerSize]);

    return (
        <div
            className={styles.container}
            ref={containerRef}
            onMouseEnter={() => {
                document.documentElement.style.overflow = 'hidden'
            }}
            onMouseLeave={() => {
                document.documentElement.style.overflow = 'unset'
            }}
        >
            {
                loading ? <Spin className={styles.spin} spinning></Spin> : null
            }
            {
                imgUrl ?
                    <div className={styles.playgound} data-ratio={imgNaturalAspectRatio} style={{
                        ...playgroundSize,
                        backgroundImage: `url("${imgUrl}")`,
                    }} >
                        {
                            boundDevices?.map((device) => {
                                return <DevicePoint key={device.device?.deviceSn} device={device} />
                            })
                        }
                    </div>

                    : <div className={styles.alt}>
                        <InfoCircleTwoTone /> &nbsp;
                        未上传展会平面图
                    </div>
            }
        </div>
    )
}